import i18n from '@/i18n';

/**
 * Checkout público: la UI usa ids de selección; lo persistido es canónico (backend):
 * efectivo | transferencia | online_manual
 *
 * Tarjeta en local y MercadoPago comparten online_manual; el detalle va en
 * `online_manual_channel` ('card' | 'mercadopago') en el snapshot del pedido.
 *
 * Si `business.mercadopago_online` (OAuth del local), el checkout cobra en vivo
 * ("Pagar ahora"); si no, sigue el flujo manual ("el local enviará link").
 */

/** Ids mostrados en Radio / flujo (no son los strings guardados en payment_method). */
export const CHECKOUT_PAYMENT_SELECTION_IDS = [
  'efectivo',
  'transferencia',
  'online_manual_card',
  'online_manual_mercadopago',
];

/** Etiquetas en la UI (manual / sin OAuth). */
export const CHECKOUT_PAYMENT_LABELS = {
  efectivo: 'Efectivo',
  transferencia: 'Transferencia',
  online_manual_card: 'Tarjeta / Débito / Crédito',
  online_manual_mercadopago: 'MercadoPago (el local enviará link de pago)',
};

export const CHECKOUT_PAYMENT_LABEL_MP_ONLINE = 'Pagar ahora con Mercado Pago';

/**
 * Etiqueta según si el local tiene MP OAuth conectado.
 * Usa i18n (menú público multiidioma).
 */
export function getCheckoutPaymentLabel(selectionId, business) {
  if (
    selectionId === 'online_manual_mercadopago' &&
    (business?.mercadopago_online === true || business?.mp_oauth_connected === true)
  ) {
    return i18n.t('checkout.mpOnlineLabel', { defaultValue: CHECKOUT_PAYMENT_LABEL_MP_ONLINE });
  }

  const keyById = {
    efectivo: 'checkout.cash',
    transferencia: 'checkout.transfer',
    online_manual_card: 'checkout.card',
    online_manual_mercadopago: 'checkout.mpManualLabel',
  };
  const i18nKey = keyById[selectionId];
  if (i18nKey) {
    return i18n.t(i18nKey, { defaultValue: CHECKOUT_PAYMENT_LABELS[selectionId] });
  }
  return CHECKOUT_PAYMENT_LABELS[selectionId] || selectionId;
}

/**
 * Convierte selección de UI al par canónico para API y WhatsApp.
 * @returns {{ canonical: 'efectivo'|'transferencia'|'online_manual', onlineManualKind: 'card'|'mercadopago'|null }}
 */
export function selectionToCanonicalPayment(selectionId) {
  if (selectionId === 'efectivo') {
    return { canonical: 'efectivo', onlineManualKind: null };
  }
  if (selectionId === 'transferencia') {
    return { canonical: 'transferencia', onlineManualKind: null };
  }
  if (selectionId === 'online_manual_card') {
    return { canonical: 'online_manual', onlineManualKind: 'card' };
  }
  if (selectionId === 'online_manual_mercadopago') {
    return { canonical: 'online_manual', onlineManualKind: 'mercadopago' };
  }
  return { canonical: 'efectivo', onlineManualKind: null };
}

/**
 * @param business — objeto `business` del menú
 * @returns {{ methodIds: string[], usedFallback: boolean }}
 */
export function getEnabledPaymentMethodsForCheckout(business) {
  if (!business || typeof business !== 'object') {
    return { methodIds: ['efectivo', 'transferencia'], usedFallback: true };
  }
  // Si el local ya tiene OAuth conectado, mostrar MP aunque el toggle del panel
  // no se haya guardado / el snapshot esté un poco atrasado.
  const mpEnabled =
    business.accepts_mercadopago === true || business.mercadopago_online === true;
  const byId = {
    efectivo: business.accepts_cash !== false,
    transferencia: business.accepts_transfer !== false,
    online_manual_card: business.accepts_card === true,
    online_manual_mercadopago: mpEnabled,
  };
  const methodIds = CHECKOUT_PAYMENT_SELECTION_IDS.filter((id) => byId[id]);
  if (methodIds.length > 0) {
    return { methodIds, usedFallback: false };
  }
  return { methodIds: ['efectivo', 'transferencia'], usedFallback: true };
}

/** True si el checkout debe redirigir a Checkout Pro tras registrar el pedido. */
export function shouldRedirectToMercadoPagoOnline(business, onlineManualKind) {
  return (
    onlineManualKind === 'mercadopago' &&
    (business?.mercadopago_online === true || business?.mp_oauth_connected === true)
  );
}
